import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, AlertCircle, CheckCircle2, Check, X, Loader2 } from 'lucide-react';
import AutoxiumLogo from '../components/AutoxiumLogo';
import { cn, apiErrorMessage } from '../lib/utils';
import {
  isValidEmail, isValidName, isValidMobile, checkPasswordRules, passwordStrength,
} from '../lib/registrationValidation';

// All validation here is for UX only (instant feedback + disabled buttons). The
// backend enforces the same rules on /auth/send-otp and /auth/register -- see the
// note at the top of lib/registrationValidation.js.

const EMAIL_DEBOUNCE_MS = 300;

// #D32F2F on white is ~5:1 contrast, which passes WCAG AA for normal text. The
// theme's `destructive` red (#EF4444) is only ~3.8:1, so it isn't used here.
const ERROR_TEXT = 'text-[#D32F2F]';
const ERROR_BORDER = 'border-[#D32F2F] focus:border-[#D32F2F]';
const INPUT_BASE = 'h-12 border-2 focus:border-autoxium-blue';

// The shared Button sets pointer-events:none when disabled, which would hide the
// not-allowed cursor -- re-enable pointer events so it shows. A natively disabled
// button still ignores clicks.
const PRIMARY_BUTTON =
  'w-full h-12 bg-autoxium-blue hover:bg-autoxium-blue/90 font-bold uppercase tracking-wider ' +
  'disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-50';

const STRENGTH_STYLES = {
  weak: { label: 'Weak', bar: 'bg-[#D32F2F]', text: ERROR_TEXT, width: 'w-1/3' },
  medium: { label: 'Medium', bar: 'bg-orange-500', text: 'text-orange-700', width: 'w-2/3' },
  strong: { label: 'Strong', bar: 'bg-emerald-600', text: 'text-emerald-700', width: 'w-full' },
};

// Message area below a field: an error (red, with an icon so color isn't the only
// signal) or a success note (green). It stays mounted as an aria-live region so
// screen readers announce changes. Height animates 0 -> content with the
// grid-template-rows 0fr/1fr technique, so the form below slides instead of
// jumping. A fixed reserved gap wouldn't work here because several messages
// wrap to two lines at phone width.
const FieldMessage = ({ id, error, success }) => {
  const message = error || success;
  return (
    <div
      id={id}
      aria-live="polite"
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
        message ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <div className="overflow-hidden">
        {message && (
          <p className={cn('flex items-start gap-1.5 pt-1 text-xs font-medium', error ? ERROR_TEXT : 'text-emerald-700')}>
            {error
              ? <AlertCircle className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />
              : <CheckCircle2 className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />}
            <span>{message}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const PasswordToggle = ({ shown, onToggle, label }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={shown ? `Hide ${label}` : `Show ${label}`}
    aria-pressed={shown}
    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
  >
    {shown ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
  </button>
);

// Steps: 1 = enter email or verification code is sent, 2 = enter the 6-digit
// code, 3 = the rest of the profile (name/mobile/password). No account row
// exists in the database until step 3 succeeds -- register() requires the
// verify_token minted by step 2's verify-otp call.
const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, sendOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mobile_number: '',
    password: '',
    confirm_password: ''
  });

  const from = location.state?.from || '/';
  const redirectMessage = location.state?.message;

  // ---------------------------------------------------------------------------
  // Step 1: email validation
  // ---------------------------------------------------------------------------
  // `checkedEmail` is the value the red/green feedback is based on. It follows
  // `email` after a 300ms typing pause, and immediately on blur. The field counts
  // as "touched" once the user has typed or left it, so an untouched empty field
  // shows no error on page load.
  const [checkedEmail, setCheckedEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setCheckedEmail(email), EMAIL_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [email]);

  const checkedEmailValid = isValidEmail(checkedEmail);
  const showEmailError = emailTouched && !checkedEmailValid;
  const showEmailValid = emailTouched && checkedEmailValid && checkedEmail === email;
  // The button uses the current value, not the debounced one. Otherwise it could
  // stay enabled for up to 300ms after the email became invalid again.
  const canSendOtp = isValidEmail(email) && !loading;

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailTouched(true);
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setCheckedEmail(email); // validate right away on blur, skipping the debounce
  };

  // ---------------------------------------------------------------------------
  // Step 3: profile validation
  // ---------------------------------------------------------------------------
  // Everything is recomputed from formData on every render, so validity always
  // reflects the current values, and "Create Account" disables again the moment
  // any field becomes invalid. `touched` only controls when an error is shown:
  // after the user leaves a field, or after a submit attempt.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Explicit, unticked-by-default agreement to the Terms and Privacy Policy
  // (DPDP Act 2023 consent needs a clear affirmative action).
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const passwordRules = checkPasswordRules(formData.password);
  const rulesMet = passwordRules.filter((r) => r.met).length;
  const strength = STRENGTH_STYLES[passwordStrength(rulesMet)];

  const valid = {
    first_name: isValidName(formData.first_name),
    last_name: isValidName(formData.last_name),
    mobile_number: isValidMobile(formData.mobile_number),
    password: rulesMet === passwordRules.length,
    // Exact comparison, with no trimming: the passwords must be identical.
    confirm_password: formData.confirm_password !== '' && formData.confirm_password === formData.password,
    terms: acceptedTerms,
  };
  const canCreateAccount = Object.values(valid).every(Boolean) && !loading;

  const isShown = (field) => touched[field] || submitAttempted;

  // Confirm Password is compared on every keystroke. The mismatch shows as soon as
  // the user starts typing there, not only after blur. Because this is derived from
  // formData, it also re-checks whenever the Password field changes.
  const confirmStarted = formData.confirm_password !== '' || isShown('confirm_password');
  let confirmError = null;
  if (confirmStarted && !valid.confirm_password) {
    confirmError = formData.confirm_password === '' ? 'Please confirm your password' : 'Passwords do not match';
  }

  const errors = {
    first_name: isShown('first_name') && !valid.first_name ? 'Please enter a valid first name' : null,
    last_name: isShown('last_name') && !valid.last_name ? 'Please enter a valid last name' : null,
    mobile_number: isShown('mobile_number') && !valid.mobile_number
      ? 'Please enter a valid mobile number (e.g., +1234567890)' : null,
    confirm_password: confirmError,
  };
  const passwordShowsError = isShown('password') && !valid.password;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => (prev[name] ? prev : { ...prev, [name]: true }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    // Guard against a submit that slips past the disabled button (e.g. Enter in a
    // form mid-debounce), and against double submission while a send is running.
    if (loading) return;
    if (!isValidEmail(email)) {
      setEmailTouched(true);
      setCheckedEmail(email);
      return;
    }
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);
    setLoading(true);
    try {
      await sendOtp(trimmedEmail);
      toast.success('Verification code sent to your email');
      setStep(2);
    } catch (err) {
      console.error('Send OTP error:', err);
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const token = await verifyOtp(email, otpCode);
      setVerifyToken(token);
      toast.success('Email verified');
      setStep(3);
    } catch (err) {
      console.error('Verify OTP error:', err);
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await sendOtp(email);
      setOtpCode('');
      toast.success('A new code has been sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // The button is disabled until every field is valid, so this only catches
    // submits that get around it. Mark every field as shown so the user can see
    // what's wrong.
    if (!Object.values(valid).every(Boolean)) {
      setSubmitAttempted(true);
      return;
    }

    setLoading(true);
    try {
      await register({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email,
        mobile_number: formData.mobile_number.trim() || null,
        password: formData.password,
        verify_token: verifyToken
      });
      toast.success('Registration successful!');
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Registration error:', err);
      toast.error(apiErrorMessage(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="register-page">
      <Card className="w-full max-w-md border-2 border-structure shadow-xl">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-block mx-auto mb-6">
            <AutoxiumLogo width={80} height={64} />
          </Link>
          <h1 className="font-chivo text-2xl uppercase tracking-tight text-chassis">Register</h1>
          <p className="text-sm text-muted-foreground">
            {step === 1 && 'Create your AUTOXIUM account'}
            {step === 2 && `Enter the code sent to ${email}`}
            {step === 3 && 'Complete your profile'}
          </p>
        </CardHeader>
        <CardContent>
          {redirectMessage && step === 1 && (
            <p
              className="text-sm text-autoxium-blue bg-autoxium-blue/10 border border-autoxium-blue/20 rounded-md px-3 py-2 mb-4 text-center"
              data-testid="register-redirect-message"
            >
              {redirectMessage}
            </p>
          )}

          {step === 1 && (
            <form onSubmit={handleSendOtp} noValidate className="space-y-4" data-testid="register-email-step">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    aria-invalid={showEmailError}
                    aria-describedby="email-message"
                    className={cn(
                      INPUT_BASE, 'pr-10',
                      showEmailError && ERROR_BORDER,
                      showEmailValid && 'border-emerald-600'
                    )}
                    data-testid="register-email-input"
                  />
                  {showEmailValid && (
                    <CheckCircle2
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-600"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <FieldMessage
                  id="email-message"
                  error={showEmailError ? 'Please verify the entered email address (e.g., john@example.com).' : null}
                />
              </div>
              <Button
                type="submit"
                className={PRIMARY_BUTTON}
                disabled={!canSendOtp}
                aria-disabled={!canSendOtp}
                aria-busy={loading}
                data-testid="register-send-otp-btn"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" aria-hidden="true" /> Sending Code...</>
                ) : 'Send Verification Code'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="register-otp-step">
              <button
                type="button"
                onClick={() => { setStep(1); setOtpCode(''); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="register-otp-back-btn"
              >
                <ArrowLeft className="h-4 w-4" /> Change email
              </button>
              <div className="flex justify-center py-2">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={setOtpCode}
                  data-testid="register-otp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-autoxium-blue hover:bg-autoxium-blue/90 font-bold uppercase tracking-wider"
                disabled={loading}
                data-testid="register-verify-otp-btn"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={loading}
                className="w-full text-center text-sm text-autoxium-blue hover:underline"
                data-testid="register-resend-otp-btn"
              >
                Didn't get a code? Resend
              </button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="register-details-step">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    autoComplete="given-name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={!!errors.first_name}
                    aria-describedby="first_name-message"
                    className={cn(INPUT_BASE, errors.first_name && ERROR_BORDER)}
                    data-testid="register-firstname-input"
                  />
                  <FieldMessage id="first_name-message" error={errors.first_name} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    autoComplete="family-name"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={!!errors.last_name}
                    aria-describedby="last_name-message"
                    className={cn(INPUT_BASE, errors.last_name && ERROR_BORDER)}
                    data-testid="register-lastname-input"
                  />
                  <FieldMessage id="last_name-message" error={errors.last_name} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile_number">Mobile Number (Optional)</Label>
                <Input
                  id="mobile_number"
                  name="mobile_number"
                  type="tel"
                  autoComplete="tel"
                  placeholder="+1234567890"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={!!errors.mobile_number}
                  aria-describedby="mobile_number-message"
                  className={cn(INPUT_BASE, errors.mobile_number && ERROR_BORDER)}
                  data-testid="register-mobile-input"
                />
                <FieldMessage id="mobile_number-message" error={errors.mobile_number} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Min 8 characters"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={passwordShowsError}
                    aria-describedby="password-strength password-rules"
                    className={cn(INPUT_BASE, 'pr-10', passwordShowsError && ERROR_BORDER)}
                    data-testid="register-password-input"
                  />
                  <PasswordToggle
                    shown={showPassword}
                    onToggle={() => setShowPassword(!showPassword)}
                    label="password"
                  />
                </div>

                {/* Strength meter: a colored bar plus a text label, so color isn't
                    the only signal. It stays empty until the user types, so an empty
                    field doesn't show "Weak". The label is announced only when the
                    level changes. */}
                <div className="flex items-center gap-3 pt-1" data-testid="register-password-strength">
                  <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-200 motion-reduce:transition-none',
                        formData.password ? cn(strength.bar, strength.width) : 'w-0'
                      )}
                    />
                  </div>
                  <span
                    id="password-strength"
                    aria-live="polite"
                    className={cn('text-xs font-semibold w-14 text-right', formData.password ? strength.text : 'text-muted-foreground')}
                  >
                    {formData.password ? strength.label : ''}
                    <span className="sr-only">{formData.password ? ' password strength' : ''}</span>
                  </span>
                </div>

                {/* Live rules checklist, always visible (so the space is fixed). An
                    unmet rule shows a grey ✗ while the user is typing and turns red
                    once the field has been left. A met rule shows a green ✓. */}
                <ul id="password-rules" className="space-y-1 text-xs" data-testid="register-password-rules">
                  {passwordRules.map((rule) => (
                    <li
                      key={rule.id}
                      data-met={rule.met}
                      className={cn(
                        'flex items-start gap-1.5',
                        rule.met ? 'text-emerald-700' : passwordShowsError ? ERROR_TEXT : 'text-muted-foreground'
                      )}
                    >
                      {rule.met
                        ? <Check className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />
                        : <X className="h-3.5 w-3.5 mt-px shrink-0" aria-hidden="true" />}
                      <span>
                        {rule.label}
                        <span className="sr-only">{rule.met ? ' (met)' : ' (not met)'}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password *</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={!!errors.confirm_password}
                    aria-describedby="confirm_password-message"
                    className={cn(INPUT_BASE, 'pr-10', errors.confirm_password && ERROR_BORDER)}
                    data-testid="register-confirm-password-input"
                  />
                  <PasswordToggle
                    shown={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                    label="confirm password"
                  />
                </div>
                <FieldMessage
                  id="confirm_password-message"
                  error={errors.confirm_password}
                  success={valid.confirm_password ? 'Passwords match' : null}
                />
              </div>
              <div className="flex items-start gap-2.5">
                <input
                  id="accept_terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-autoxium-blue cursor-pointer"
                  data-testid="register-accept-terms"
                />
                <label htmlFor="accept_terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                  I am 18 or older and agree to the{' '}
                  <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-autoxium-blue font-medium hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-autoxium-blue font-medium hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>
              <Button
                type="submit"
                className={PRIMARY_BUTTON}
                disabled={!canCreateAccount}
                aria-disabled={!canCreateAccount}
                aria-busy={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" aria-hidden="true" /> Creating Account...</>
                ) : 'Create Account'}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link
              to="/login"
              state={location.state}
              className="text-autoxium-blue hover:underline font-medium"
              data-testid="login-link"
            >
              Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
